// Roteamento central de erro (`spec/17-frontend.md` §4.3). Decide **sempre** pelo
// `code`, nunca pela `message` e nunca pelo header `X-MFA-Step-Up` (que não está em
// `exposeHeaders` do CORS e o navegador não entrega).

import type { QueryClient } from '@tanstack/react-query';
import { isApiError } from '../api/errors.ts';
import { SESSION_QUERY_KEY, type SessionUser } from '../api/session.ts';
import { type Surface, surface } from './surface.ts';

export type GateDestination =
  | { kind: 'login'; next: string; reason?: LoginReason }
  | { kind: 'navigate'; to: string }
  | { kind: 'no-surface-access' }
  | { kind: 'none' };

/** Por que a sessão acabou, quando a tela de login precisa explicar. */
export type LoginReason = 'ip-changed';

/**
 * A sessão do admin é presa ao IP (`spec/03` §4.7,
 * `apps/api/src/middlewares/auth-session.ts`): com VPN ou iCloud Private Relay o IP muda
 * entre o login e a primeira chamada, a API APAGA a sessão e devolve `401
 * session_required`, o mesmo `code` de "expirou" e de "nunca entrou". O `code` não
 * separa os três casos e o header `X-MFA-Step-Up` não chega (não está em `exposeHeaders`),
 * então a `message` é o único sinal.
 *
 * A exceção é estreita de propósito: a `message` NÃO escolhe o destino (é `/login` nos
 * três casos), só decide se a tela de login explica o motivo. Nenhuma regra de navegação
 * depende dela.
 */
const IP_CHANGED = /ip changed/i;

export interface GateContext {
  surface: Surface;
  /** De `GET /v1/me/auth/get-session`; `undefined` enquanto a sessão não carregou. */
  twoFactorEnabled: boolean | undefined;
  /** Rota atual, para o `?next=` do login. */
  currentPath: string;
}

/**
 * `403 mfa_required` bifurca por `two_factor_enabled`: sem 2FA o usuário vai habilitar,
 * com 2FA quem resolve é o `StepUpDialog` (já tratado dentro de `api/client.ts`), então
 * aqui o destino é `none` e a tela mostra o erro.
 */
export function destinationFor(error: unknown, ctx: GateContext): GateDestination {
  if (!isApiError(error)) {
    return { kind: 'none' };
  }
  switch (error.code) {
    case 'session_required':
      return IP_CHANGED.test(error.message)
        ? { kind: 'login', next: ctx.currentPath, reason: 'ip-changed' }
        : { kind: 'login', next: ctx.currentPath };
    case 'terms_not_accepted':
      return { kind: 'navigate', to: '/app/terms' };
    case 'password_change_required':
      return { kind: 'navigate', to: '/admin/change-password' };
    // Nunca `/login`: o seller que caiu no host de admin entraria em laço.
    case 'insufficient_role':
      return { kind: 'no-surface-access' };
    // AUTH-05: só o admin sem 2FA é mandado para habilitar. O seller sem 2FA confirma
    // com a senha, e quem resolve isso é o `StepUpDialog` (dentro de `api/client.ts`),
    // então o destino aqui é `none` nos dois estados de 2FA do seller.
    case 'mfa_required':
      if (ctx.twoFactorEnabled === false && ctx.surface === 'admin') {
        return { kind: 'navigate', to: '/admin/setup-2fa' };
      }
      return { kind: 'none' };
    default:
      return { kind: 'none' };
  }
}

export function loginHref(next: string, reason?: LoginReason): string {
  const query = `next=${encodeURIComponent(next)}`;
  return reason === undefined ? `/login?${query}` : `/login?${query}&reason=${reason}`;
}

// Ponte com o roteador: o `QueryCache` vive fora do React, então o `__root` registra o
// `navigate` na montagem e o cache chama `handleGateError` em todo erro.
let navigateTo: ((to: string) => void) | undefined;

export function registerGateNavigate(fn: ((to: string) => void) | undefined): void {
  navigateTo = fn;
}

export function handleGateError(error: unknown, queryClient: QueryClient): void {
  const session = queryClient.getQueryData<SessionUser | null>(SESSION_QUERY_KEY);
  const destination = destinationFor(error, {
    surface: surface(),
    twoFactorEnabled: session?.twoFactorEnabled,
    currentPath: window.location.pathname + window.location.search,
  });

  if (destination.kind === 'none' || navigateTo === undefined) {
    return;
  }
  if (destination.kind === 'login') {
    void queryClient.setQueryData(SESSION_QUERY_KEY, null);
    navigateTo(loginHref(destination.next, destination.reason));
    return;
  }
  if (destination.kind === 'no-surface-access') {
    navigateTo('/no-access');
    return;
  }
  navigateTo(destination.to);
}
